"use client"

import { router } from "@inertiajs/react"
import * as React from "react"

import { ImagePicker } from "@/components/image-picker"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  feedPath,
  forumPath,
  marketplacePath,
  myNeighborhoodPath,
  rafflesPath,
} from "@/routes"

interface WelcomeModalProps {
  isOpen: boolean
  onClose: () => void
  neighborhoods?: { id: number; name: string }[]
  popularNeighborhoods?: { id: number; name: string }[]
  interests?: { id: number; name: string }[]
}

export function WelcomeModal({
  isOpen,
  onClose,
  neighborhoods = [],
  popularNeighborhoods = [],
  interests = [],
}: WelcomeModalProps) {
  const [step, setStep] = React.useState<1 | 2 | 3 | 4 | 5>(1)
  const [neighborhoodSearch, setNeighborhoodSearch] = React.useState("")
  const [selectedNeighborhood, setSelectedNeighborhood] = React.useState<
    number | null
  >(null)
  const [selectedInterests, setSelectedInterests] = React.useState<string[]>([])
  const [avatarFile, setAvatarFile] = React.useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = React.useState<string | null>(null)

  const completedRef = React.useRef(false)
  const lastStepRef = React.useRef<1 | 2 | 3 | 4 | 5>(1)

  const resetState = () => {
    setStep(1)
    setNeighborhoodSearch("")
    setSelectedNeighborhood(null)
    setSelectedInterests([])
    setAvatarFile(null)
    setAvatarPreview(null)
  }

  const handleClose = () => {
    // Blur any focused input to dismiss iOS keyboard and reset viewport zoom
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    onClose()
    resetState()
  }

  const trackOnboardingEvent = async (
    event: "started" | "abandoned" | "dismissed",
    stepNumber?: number,
  ) => {
    try {
      await fetch("/settings/onboarding", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") ?? "",
        },
        body: JSON.stringify({
          event,
          step: stepNumber,
          source: "welcome_modal",
        }),
        credentials: "same-origin",
      })
    } catch (error) {
      // Silent fail - tracking is not critical
      console.debug("Onboarding tracking failed:", error)
    }
  }

  const handleBack = () => {
    setStep((prev) => (prev > 1 ? ((prev - 1) as typeof step) : prev))
  }

  const toggleInterest = (id: string) => {
    setSelectedInterests((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    )
  }

  const interestsValid = selectedInterests.length >= 3

  const handleAvatarChange = (file: File) => {
    setAvatarFile(file)
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview)
    }
    setAvatarPreview(URL.createObjectURL(file))
  }

  const handleCompleteOnboarding = (redirectTo?: string) => {
    completedRef.current = true
    const formData = new FormData()

    if (selectedNeighborhood) {
      formData.append("neighborhood_id", String(selectedNeighborhood))
    }

    selectedInterests.forEach((id) => {
      formData.append("interest_ids[]", id)
    })

    if (avatarFile) {
      formData.append("avatar", avatarFile)
    }

    router.post("/settings/onboarding", formData, {
      forceFormData: true,
      onSuccess: () => {
        handleClose()
        // Delay navigation to let iOS reset viewport zoom after dialog closes
        requestAnimationFrame(() => {
          router.visit(redirectTo ?? feedPath())
        })
      },
      onError: (errors) => {
        console.error("Error completing onboarding:", errors)
      },
    })
  }

  React.useEffect(() => {
    if (!isOpen) return

    completedRef.current = false
    lastStepRef.current = 1
    void trackOnboardingEvent("started", 1)
  }, [isOpen])

  React.useEffect(() => {
    lastStepRef.current = step
  }, [step])

  const filteredNeighborhoods = React.useMemo(() => {
    if (!neighborhoodSearch.trim()) return popularNeighborhoods
    return neighborhoods.filter((n) =>
      n.name.toLowerCase().includes(neighborhoodSearch.trim().toLowerCase()),
    )
  }, [neighborhoodSearch, neighborhoods, popularNeighborhoods])

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div className="flex w-full flex-col items-center space-y-6 text-center">
          <div className="space-y-2">
            <h2
              className="text-primary text-2xl font-bold tracking-[0.35px] sm:text-3xl"
            >
              ¡Bienvenido/a a CurrAR!
            </h2>
            <p className="text-muted-foreground text-sm">
              Tu comunidad argentina en Madrid
            </p>
          </div>
          <div className="max-w-sm space-y-1 text-left text-sm">
            <p>• Descubrí eventos cerca tuyo</p>
            <p>• Encontrá negocios de confianza</p>
            <p>• Conectá con otros argentinos</p>
          </div>
          <Button className="mt-4 w-full sm:w-auto" onClick={() => setStep(2)}>
            Empezar
          </Button>
        </div>
      )
    }

    if (step === 2) {
      return (
        <div className="flex w-full flex-col gap-6">
          <div className="space-y-2 text-center">
            <h2 className="text-xl font-semibold">¿En qué barrio vivís?</h2>
            <p className="text-muted-foreground text-sm">
              Te mostraremos contenido cercano
            </p>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <input
                type="text"
                value={neighborhoodSearch}
                onChange={(e) => setNeighborhoodSearch(e.target.value)}
                placeholder="🔍 Buscar barrio..."
                className="bg-background focus-visible:ring-primary w-full rounded-xl border px-3 py-2 text-base focus-visible:ring-2 focus-visible:outline-none"
              />
            </div>

            <div className="max-h-44 space-y-3 overflow-y-auto">
              {filteredNeighborhoods.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {filteredNeighborhoods.map((neighborhood) => {
                    const isSelected = selectedNeighborhood === neighborhood.id
                    return (
                      <button
                        key={neighborhood.id}
                        type="button"
                        onClick={() => setSelectedNeighborhood(neighborhood.id)}
                        className="hover:bg-muted flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors hover:text-white"
                      >
                        <div
                          className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors ${
                            isSelected
                              ? "border-primary bg-primary"
                              : "border-muted-foreground bg-background"
                          }`}
                        >
                          {isSelected && (
                            <div className="bg-primary-foreground h-2 w-2 rounded-full" />
                          )}
                        </div>
                        <span>{neighborhood.name}</span>
                      </button>
                    )
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground py-4 text-center text-sm">
                  No se encontraron barrios
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <Button variant="outline" className="flex-1" onClick={handleBack}>
              Atrás
            </Button>
            <Button
              className="flex-1"
              onClick={() => setStep(3)}
              disabled={!selectedNeighborhood}
            >
              Continuar
            </Button>
          </div>
        </div>
      )
    }

    if (step === 3) {
      return (
        <div className="flex w-full flex-col gap-6">
          <div className="space-y-1 text-center">
            <h2 className="text-xl font-semibold">¿Qué te interesa?</h2>
            <p className="text-muted-foreground text-sm">Elegí al menos 3</p>
          </div>

          <div className="grid max-h-64 grid-cols-2 gap-2 overflow-y-auto">
            {interests.map((interest) => {
              const isSelected = selectedInterests.includes(String(interest.id))
              return (
                <button
                  key={interest.id}
                  type="button"
                  onClick={() => toggleInterest(String(interest.id))}
                  className={`flex max-w-full min-w-0 items-center justify-center truncate rounded-lg border px-2 py-2 text-xs transition-colors ${isSelected ? "bg-muted text-white" : "border-border bg-background"}`}
                >
                  <span>{interest.name}</span>
                </button>
              )
            })}
          </div>

          <p className="text-muted-foreground text-center text-xs">
            Personalizamos el feed para vos
          </p>

          <div className="mt-2 flex items-center justify-between gap-3">
            <Button variant="outline" className="flex-1" onClick={handleBack}>
              Atrás
            </Button>
            <Button
              className="flex-1"
              onClick={() => setStep(4)}
              disabled={!interestsValid}
            >
              Continuar
            </Button>
          </div>
        </div>
      )
    }

    if (step === 4) {
      return (
        <div className="flex w-full flex-col gap-6">
          <div className="space-y-2 text-center">
            <h2 className="text-xl font-semibold">Agregá una foto</h2>
            <p className="text-muted-foreground text-sm">
              Ayuda a que otros te reconozcan
            </p>
          </div>

          <ImagePicker
            onImageSelected={handleAvatarChange}
            preview={avatarPreview}
            variant="avatar"
            previewSize="md"
          />
          {avatarPreview && (
            <p className="text-muted-foreground text-center text-xs">
              Foto seleccionada. Podés cambiarla cuando quieras.
            </p>
          )}

          <div className="mt-2 flex items-center justify-between gap-3">
            <Button variant="outline" className="flex-1" onClick={handleBack}>
              Atrás
            </Button>
            <Button
              variant="ghost"
              className="flex-1"
              onClick={() => setStep(5)}
            >
              Omitir
            </Button>
            <Button className="flex-1" onClick={() => setStep(5)}>
              Listo
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className="flex w-full flex-col gap-6">
        <div className="space-y-2 text-center">
          <h2 className="text-xl font-semibold">¡Listo!</h2>
        </div>

        <div className="space-y-3">
          <p className="text-center text-sm font-medium">
            ¿Qué querés hacer primero?
          </p>
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => handleCompleteOnboarding(myNeighborhoodPath())}
            >
              <span className="mr-2">🎉</span>
              Ver eventos en mi barrio
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => handleCompleteOnboarding(rafflesPath())}
            >
              <span className="mr-2">🎁</span>
              Participar en un sorteo
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => handleCompleteOnboarding(forumPath())}
            >
              <span className="mr-2">❓</span>
              Hacer una pregunta
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => handleCompleteOnboarding(marketplacePath())}
            >
              <span className="mr-2">🏪</span>
              Descubrir negocios
            </Button>
          </div>
        </div>

        <Button
          className="mt-2 w-full"
          onClick={() => handleCompleteOnboarding(feedPath())}
        >
          Ir al feed
        </Button>
      </div>
    )
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (!completedRef.current) {
            void trackOnboardingEvent("dismissed", lastStepRef.current)
          }
          handleClose()
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogTitle className="sr-only">
          Configuración inicial de CurrAR
        </DialogTitle>
        <DialogDescription className="sr-only">
          Completa tu perfil para personalizar tu experiencia en la comunidad
          argentina de Madrid
        </DialogDescription>
        {renderStepContent()}
      </DialogContent>
    </Dialog>
  )
}
